import Logo from 'components/logo/Logo'
import PropTypes from 'prop-types'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import FontAwesome from 'react-fontawesome'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import I18n from 'resources/i18n/I18n'
import GroupApi from 'services/groupApi'
import PatrimonyApi from 'services/patrimonyApi'
import { fetchPatrimoniesSuccess } from '../../../store/actions/resourceActions'
import { errorToast, loadingToast, successToast } from '../../../utils/toast'
import { toast } from 'react-toastify'
import CustomButton from '../../buttons/CustomButton'
import Title from '../../typo/Title'
import '../configuration.scss'
import LabelEditionTreePatrimonies from './LabelEditionTreePatrimonies'
import ModalImportCsv from './ModalImportCsv'
import ModalImportResults from './ModalImportResults'

const EstateOrganizationEdition = ({ location, history }) => {
  const [error, setError] = useState(null)
  const currentUser = useSelector((state) => state.userReducer.user)
  const resources = useSelector((state) => state.resourceReducer)
  const dispatch = useDispatch()
  const [showModalCsv, setShowModalCsv] = useState(false)
  const [showResultsModal, setShowResultsModal] = useState(false)
  const [importResults, setImportResults] = useState(null)
  const [lastImportedFile, setLastImportedFile] = useState(null)
  const [searchValue, setSearchValue] = useState(null)
  const [group, setGroup] = useState(null)
  const [groupId, setGroupId] = useState(null)
  const searchTimeoutRef = useRef(null)

  useEffect(() => {
    const groupId = new URLSearchParams(location.search).get('groupId')

    if (groupId) {
      setGroupId(groupId)
      GroupApi.getGroupByCode(groupId).then(setGroup)
      getPatrimonies()
    } else if (currentUser && currentUser.group) {
      GroupApi.getGroupByCode(currentUser.group.code).then((group) => {
        setGroup(group)
        getPatrimonies()
      })
    } else if (resources.patrimonies.length === 0 && !groupId) {
      getPatrimonies()
    }
  }, [])

  const getPatrimonies = (overrideValue) => {
    PatrimonyApi.getPatrimonyList({
      groupId,
      searchValue: overrideValue !== null ? overrideValue : searchValue,
      size: 1000,
    }).then((patrimonies) => {
      dispatch(fetchPatrimoniesSuccess(patrimonies))
    })
  }

  const toggleModalImportCsv = () => {
    setShowModalCsv(!showModalCsv)
  }

  const resetLastImportedFile = () => {
    setLastImportedFile(null)
  }

  const importCsv = (item, isDryRun) => {
    setLastImportedFile(item)

    const toastId = loadingToast(I18n.t('ImportInProgress'))

    PatrimonyApi.importCsvPatrimonies(item, { groupId }, isDryRun)
      .then((response) => {
        toast.dismiss(toastId)

        const results = {
          isDryRun,
          totalProcessed: response.totalProcessed || 0,
          successCount: response.successCount || 0,
          errorCount: response.errorCount || 0,
          errors: response.errors ? response.errors.split('\n').filter((line) => line.trim()) : [],
          rawErrors: response.errors || null,
          allDataAlreadyImported: response.allDataAlreadyImported || false,
          shouldReload: !isDryRun && response.successCount > 0,
          created: response.created || {},
          skipped: response.skipped || {},
        }

        // Si c'est un import réel réussi, afficher un toast
        const hasOnlyDuplicateErrors = results.errors.every((error) => error.includes('existe déjà') || error.includes('doublon'))
        const isRealImport = !isDryRun
        const hasSuccessfulImports = results.successCount > 0

        if (isRealImport && (results.errorCount === 0 || (hasOnlyDuplicateErrors && hasSuccessfulImports))) {
          // Afficher un toast de succès pour les imports sans erreurs ou avec seulement des doublons
          const duplicateCount = hasOnlyDuplicateErrors ? results.errorCount : 0
          let message = `Import réussi ! ${results.successCount} ligne(s) importée(s).`
          if (duplicateCount > 0) {
            message += ` ${duplicateCount} doublon(s) ignoré(s).`
          }
          successToast(message)
          if (results.shouldReload) {
            setTimeout(() => {
              window.location.reload()
            }, 2000)
          }
        } else {
          setImportResults(results)
          setShowResultsModal(true)
        }
      })
      .catch((error) => {
        toast.dismiss(toastId)
        console.error('Import CSV error:', error)
        errorToast(I18n.t('ErrorImport'))
      })
  }

  const proceedToImport = () => {
    setShowResultsModal(false)
    if (lastImportedFile) {
      importCsv(lastImportedFile, false)
    }
  }

  const createPatrimony = (item) =>
    PatrimonyApi.createPatrimony({ label: item.label, code: item.code, idGroup: groupId || currentUser.group.code })
      .then(() => {
        getPatrimonies()
      })
      .catch((e) => e)

  const editPatrimony = (item) =>
    PatrimonyApi.updatePatrimony(item)
      .then(() => getPatrimonies())
      .catch((e) => e)

  const deletePatrimony = (item) => {
    setError(null)
    PatrimonyApi.deletePatrimony(item)
      .then(() => getPatrimonies())
      .catch(() => setError(I18n.t('ErrorDelete')))
  }

  const createSector = (item) => {
    return PatrimonyApi.createSector({ label: item.label, code: item.code, idPatrimony: item.idParent })
      .then(() => getPatrimonies())
      .catch((e) => e)
  }

  const editSector = (item) =>
    PatrimonyApi.updateSector(item)
      .then(() => getPatrimonies())
      .catch((e) => e)

  const deleteSector = (item) => {
    setError(null)
    PatrimonyApi.deleteSector(item)
      .then(() => getPatrimonies())
      .catch(() => setError(I18n.t('ErrorDelete')))
  }

  const createAgency = (item) =>
    PatrimonyApi.createAgency({ label: item.label, code: item.code, idSector: item.idParent })
      .then(() => getPatrimonies())
      .catch((e) => e)

  const editAgency = (item) =>
    PatrimonyApi.updateAgency(item)
      .then(() => getPatrimonies())
      .catch((e) => e)

  const deleteAgency = (item) => {
    setError(null)
    PatrimonyApi.deleteAgency(item)
      .then(() => getPatrimonies())
      .catch(() => setError(I18n.t('ErrorDelete')))
  }

  const goBack = () => history.push('/configuration')

  const debouncedSearch = useCallback((value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchValue(value)
      getPatrimonies(value)
    }, 300)
  }, [])

  const onSearchValueChange = useCallback(
    (event) => {
      const value = event.target.value

      if (value === '' && searchValue && searchValue.length > 0) {
        setSearchValue('')
        getPatrimonies('')
        return
      }

      if (value.length > 2 || (searchValue && searchValue.length)) {
        debouncedSearch(value)
      }
    },
    [searchValue, debouncedSearch],
  )

  return (
    <div className="container-fluid estate-configuration">
      <div className="row justify-content-center">
        <Logo />
      </div>
      <div className="container">
        <div className="row m-0 justify-content-between">
          <Title
            text={`${I18n.t('EstateConfiguration')}${group && currentUser.roles.indexOf('ORFI-ADMIN') > -1 ? ` : ${group.name || 'Nom inconnu'}` : ''}`}
          />
        </div>
        <LabelEditionTreePatrimonies
          patrimonies={resources.patrimonies || []}
          createPatrimony={createPatrimony}
          editPatrimony={editPatrimony}
          deletePatrimony={deletePatrimony}
          createSector={createSector}
          editSector={editSector}
          deleteSector={deleteSector}
          createAgency={createAgency}
          editAgency={editAgency}
          deleteAgency={deleteAgency}
          importPatrimony={toggleModalImportCsv}
          error={error}
          group={group}
          onSearchValueChange={onSearchValueChange}
          searchValue={searchValue}
        />
        <div className="row justify-content-center p-4">
          <CustomButton onClick={goBack} icon={<FontAwesome name="arrow-left" />} small secondary />
        </div>
      </div>
      <ModalImportCsv
        showModal={showModalCsv}
        toggleModal={toggleModalImportCsv}
        importCsv={importCsv}
        type="patrimonies"
        onFileChange={resetLastImportedFile}
      />
      <ModalImportResults
        show={showResultsModal}
        onClose={() => setShowResultsModal(false)}
        results={importResults}
        isDryRun={importResults?.isDryRun}
        onProceedToImport={proceedToImport}
      />
    </div>
  )
}

EstateOrganizationEdition.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
}

export default withRouter(EstateOrganizationEdition)
